// ytdModuleCompletion.js — Modal content for YTD Module Completion KPI
// Shows monthly cumulative completion vs target with a line chart.
// Place at: src/components/modalContent/productionMilestonesModalComponents/ytdModuleCompletion.js

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// Inline mock — swap for import from mockProductionMilestonesData when added
const YTD_MONTHLY = [
  { month: "Jan", completed: 38, target: 40, cumCompleted: 38, cumTarget: 40 },
  { month: "Feb", completed: 44, target: 40, cumCompleted: 82, cumTarget: 80 },
  {
    month: "Mar",
    completed: 42,
    target: 40,
    cumCompleted: 124,
    cumTarget: 120,
  },
  {
    month: "Apr",
    completed: 40,
    target: 40,
    cumCompleted: 164,
    cumTarget: 160,
  },
  {
    month: "May",
    completed: 39,
    target: 40,
    cumCompleted: 203,
    cumTarget: 200,
  },
  {
    month: "Jun",
    completed: 40,
    target: 40,
    cumCompleted: 243,
    cumTarget: 240,
  },
  {
    month: "Jul",
    completed: 40,
    target: 40,
    cumCompleted: 283,
    cumTarget: 280,
  },
  {
    month: "Aug",
    completed: 45,
    target: 40,
    cumCompleted: 328,
    cumTarget: 320,
  },
  {
    month: "Sep",
    completed: 43,
    target: 40,
    cumCompleted: 371,
    cumTarget: 360,
  },
  {
    month: "Oct",
    completed: 40,
    target: 40,
    cumCompleted: 411,
    cumTarget: 400,
  },
  {
    month: "Nov",
    completed: 40,
    target: 40,
    cumCompleted: 451,
    cumTarget: 440,
  },
  {
    month: "Dec",
    completed: 39,
    target: 40,
    cumCompleted: 490,
    cumTarget: 480,
  },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        Completed: {d.cumCompleted}
      </div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#1a1a1a" }}
        />
        Target: {d.cumTarget}
      </div>
    </div>
  );
};

const YtdModuleCompletion = ({ goals }) => {
  const target = goals?.ytdCompletion || 95.0;
  const latest = YTD_MONTHLY[YTD_MONTHLY.length - 1];
  const ytdPct = latest.cumTarget
    ? ((latest.cumCompleted / latest.cumTarget) * 100).toFixed(1)
    : "0";
  const deltaVsTarget = latest.cumCompleted - latest.cumTarget;
  const aheadBehind =
    deltaVsTarget >= 0
      ? `+${deltaVsTarget} ahead of YTD target`
      : `${Math.abs(deltaVsTarget)} behind YTD target`;

  const monthlyAvg = (
    YTD_MONTHLY.reduce((s, d) => s + d.completed, 0) / YTD_MONTHLY.length
  ).toFixed(1);

  const bestMonth = YTD_MONTHLY.reduce((a, b) =>
    b.completed > a.completed ? b : a,
  );
  const missedMonths = YTD_MONTHLY.filter((d) => d.completed < d.target).length;

  return (
    <div className="pml-modal-flex-content">
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">YTD Completion</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${parseFloat(ytdPct) >= target ? "ok" : "alert"}`}
          >
            {ytdPct}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">vs Target</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${deltaVsTarget >= 0 ? "ok" : "alert"}`}
          >
            {aheadBehind.split(" ")[0]}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Monthly Avg</span>
          <span className="pml-modal-stat-value">{monthlyAvg}</span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Months Missed</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${missedMonths === 0 ? "ok" : "alert"}`}
          >
            {missedMonths}
          </span>
        </div>
      </div>

      <p className="pml-modal-sub-title">Cumulative Completion vs Target</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={YTD_MONTHLY}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Black target line */}
            <Line
              type="monotone"
              dataKey="cumTarget"
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              name="Target"
            />
            <Line
              type="monotone"
              dataKey="cumCompleted"
              stroke="#cc0000"
              strokeWidth={2}
              dot={{ r: 4, fill: "#cc0000", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              name="Completed"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="pml-modal-sub-title">Monthly Breakdown</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <th>Month</th>
              <th style={{ textAlign: "center" }}>Target</th>
              <th style={{ textAlign: "center" }}>Completed</th>
              <th style={{ textAlign: "center" }}>Delta</th>
              <th style={{ textAlign: "center" }}>Cum. Completed</th>
              <th style={{ textAlign: "center" }}>Cum. Target</th>
            </tr>
          </thead>
          <tbody>
            {YTD_MONTHLY.map((d) => {
              const delta = d.completed - d.target;
              return (
                <tr key={d.month}>
                  <td style={{ fontWeight: 600 }}>{d.month}</td>
                  <td style={{ textAlign: "center" }}>{d.target}</td>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>
                    {d.completed}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      color: delta >= 0 ? "#38a169" : "#cc0000",
                    }}
                  >
                    {delta >= 0 ? `+${delta}` : delta}
                  </td>
                  <td style={{ textAlign: "center" }}>{d.cumCompleted}</td>
                  <td style={{ textAlign: "center", color: "#718096" }}>
                    {d.cumTarget}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: "#a0aec0" }}>
        Best month: {bestMonth.month} ({bestMonth.completed} modules completed)
      </div>
    </div>
  );
};

export default YtdModuleCompletion;
